import { memo } from 'react'
import { SxProps, Skeleton, ButtonBase } from '@mui/material'
import { Box } from '@/components/Layout'
import { Divider, Price, Text } from '@/components/Data'
import { FanaticsCash } from '@/components/Composed'
import { ActionButtons } from '@/components/Inputs'
import { ArrowLongIcon } from '@/assets'
import { globalClasses } from '@/styles'
import { MIN_AMOUNT_FOR_SAVINGS_LABEL, useDomainInfo } from '@/utils'
import { packageSummaryStyles as styles } from './PackageSummary.style'
import { usePackageSummaryController } from './PackageSummary.controller'

export interface PackageSummaryProps {
  /**
   * If true, shows the continue button at the bottom of the summary.
   */
  showContinueButton?: boolean

  /**
   * Indicates whether additional space should be reserved for a "Continue" button in the user interface.
   * When set to true, the layout adjusts to provide adequate spacing for the button.
   * Optional; defaults to undefined if not explicitly provided.
   */
  addContinueButtonSpace?: boolean

  /**
   * If true, displays loading skeletons for price values.
   */
  priceLoading?: boolean

  /**
   * If provided, renders a "Show details" link next to the heading that calls this callback.
   */
  onShowDetails?: () => void
}

function PackageSummary({
  addContinueButtonSpace = false,
  showContinueButton = false,
  priceLoading = false,
  onShowDetails,
}: PackageSummaryProps) {
  const {
    t,
    userCurrency,
    currentPackage,
    productCount,
    dueAtProperty,
    savings,
    packageCurrency,
    showActualCharge,
    actualChargeFormatted,
    onContinueClick,
  } = usePackageSummaryController()

  const { isFanaticsDomain } = useDomainInfo()

  if (productCount === 0 || !currentPackage) return null

  const { hotelInfo, flightInfo, cartInfo } = currentPackage
  const addons = cartInfo.addons ?? []

  return (
    <Box sx={[styles.wrapper, addContinueButtonSpace && styles.stickyWrapper] as SxProps}>
      <Text sx={styles.heading}>{t('totalPackage')}</Text>

      <Box sx={styles.row}>
        <Text sx={styles.people}>{t('pricePerPerson')}</Text>
        <Text sx={styles.people}>
          {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
          {!priceLoading && (
            <Price value={cartInfo.pricing?.perPerson?.subTotal ?? 0} precision={2} />
          )}
        </Text>
      </Box>

      <Box sx={styles.breakdown}>
        {cartInfo.ticket && (
          <Box sx={styles.row}>
            <Text sx={styles.value} className={globalClasses.ellipsis}>
              {currentPackage.event.name}
            </Text>
          </Box>
        )}

        {addons.length > 0 &&
          addons.map((addon) => (
            <Box sx={styles.row} key={addon.details.id}>
              <Text sx={styles.value} className={globalClasses.ellipsis}>
                {addon.details.name}
              </Text>
            </Box>
          ))}

        {cartInfo.shipment && (
          <Box sx={styles.row}>
            <Text sx={styles.value} className={globalClasses.ellipsis}>
              {cartInfo.shipment.name}
            </Text>
          </Box>
        )}
        {cartInfo.hotel && (
          <Box sx={styles.row}>
            <Text sx={styles.value} className={globalClasses.ellipsis}>
              {hotelInfo?.name ?? t('hotels')}
            </Text>
          </Box>
        )}
        {cartInfo.flight && (
          <Box sx={styles.row}>
            <Text sx={styles.value} className={globalClasses.ellipsis}>
              {flightInfo ? (
                <>
                  {`${flightInfo.airlines[flightInfo.departure.operatingAirline]?.name ?? ''} ${flightInfo.departure.airport.departure} `}
                  <Box component={ArrowLongIcon} sx={styles.flightArrow} />
                  {` ${flightInfo.departure.airport.destination}`}
                </>
              ) : (
                t('flights')
              )}
            </Text>
          </Box>
        )}
        {cartInfo.insurance && (
          <Box sx={styles.row}>
            <Text sx={styles.value} className={globalClasses.ellipsis}>
              {t('refundableBooking')}
            </Text>
          </Box>
        )}
        {onShowDetails && (
          <Box sx={styles.row}>
            <ButtonBase sx={styles.showDetailsButton} onClick={onShowDetails}>
              <Text sx={styles.showDetailsText}>{t('showDetails')}</Text>
            </ButtonBase>
          </Box>
        )}
      </Box>

      <Box sx={styles.row}>
        <Text sx={styles.people}>{t('noOfPeople')}</Text>
        <Text sx={styles.people}>{`X${cartInfo.pricing?.persons}`}</Text>
      </Box>
      <Box sx={styles.feeRow}>
        <Text sx={styles.tax}>{t('taxesAndFees')}</Text>
        <Text sx={styles.tax}>
          {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
          {!priceLoading && (
            <Price value={cartInfo.pricing?.total?.totalTaxes ?? 0} precision={2} />
          )}
        </Text>
      </Box>
      <Divider sx={styles.divider} />

      <Box sx={styles.feeRow}>
        <Text sx={styles.people}>{t('total', { currency: userCurrency })}</Text>
        <Text sx={styles.people}>
          {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
          {!priceLoading && <Price value={cartInfo.pricing?.total?.total ?? 0} precision={2} />}
        </Text>
      </Box>

      {savings >= MIN_AMOUNT_FOR_SAVINGS_LABEL && (
        <Box sx={styles.feeRow}>
          <Text sx={styles.savings}>{t('savings')}</Text>
          <Text sx={styles.savings}>
            {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
            {!priceLoading && (
              <>
                -<Price value={savings} />
              </>
            )}
          </Text>
        </Box>
      )}

      {dueAtProperty > 0 && (
        <Box sx={styles.feeRow}>
          <Text sx={styles.tax}>{t('dueAtProperty')}</Text>
          <Text sx={styles.tax}>
            {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
            {!priceLoading && <Price value={dueAtProperty} precision={2} />}
          </Text>
        </Box>
      )}

      {showActualCharge && (
        <Text sx={styles.actualCharge}>
          {priceLoading && <Skeleton variant="text" sx={styles.priceLoader} />}
          {!priceLoading &&
            t('actualCharge', { amount: actualChargeFormatted, currency: packageCurrency })}
        </Text>
      )}

      {isFanaticsDomain && (
        <Box sx={styles.fanaticsCashRow}>
          <FanaticsCash />
        </Box>
      )}

      {showContinueButton && (
        <ActionButtons
          primaryActionText={t('continueButton')}
          primaryButtonProps={{ sx: styles.continueButton }}
          bordered
          fixedInMobile
          onPrimaryActionClick={onContinueClick}
        />
      )}
    </Box>
  )
}

export default memo(PackageSummary)
